"use client";

// Third-party Imports
import { createSlice, createAsyncThunk, current } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

// Type Imports
import type { entityUsersTypes } from "@/types/apps/EntityUsers";

// Server Imports
import { doGetEntityUsers } from "@/app/server/actions";

const initialState: entityUsersTypes = {
  users: [],
  filteredUsers: [],
  loading: "idle" as string | "pending" as string | "succeeded" as string | "failed" as string,
  error: null,
  total: 0,
  lastErrorToastTime: 0,
} as any;

export const fetchUsers = createAsyncThunk<any, any, any>("manage_users/fetch_users", async (_, { rejectWithValue }) => {
  const response = await doGetEntityUsers();

  // If response is null, it means a 500 error occurred
  if (response === null || response === undefined) {
    return rejectWithValue("Failed to load users");
  }

  // Ensure response is an array
  const users = Array.isArray(response) ? response : [];

  return {
    data: users,
    filtered: users,
    total: users.length,
  };
});

export const entityManageUsersSlice = createSlice({
  name: "manageUsers",
  initialState: initialState,
  reducers: {
    filterGeneral: (state, action) => {
      const { search } = action.payload;

      const currentState = current(state);
      let data = currentState.filteredUsers;

      if (search && search.length > 0) {
        data = data.filter((user: any) => {
          const fullname = JSON.parse(user.details);

          return fullname.fullname.toLowerCase().includes(search.toLowerCase()) || user.email.toLowerCase().includes(search.toLowerCase());
        });
      }

      state.users = data;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchUsers.pending, state => {
      state.loading = "pending";
    });
    builder.addCase(fetchUsers.rejected, (state, action) => {
      // Don't show error if the request was aborted
      if (action.meta.aborted) {
        state.loading = "idle";
        return;
      }

      state.loading = "failed";
      state.error = action.payload;

      // Only show toast if there's an actual error and enough time has passed since last toast
      // This prevents duplicates in React Strict Mode (500ms debounce)
      const now = Date.now();
      const timeSinceLastToast = now - ((state as any).lastErrorToastTime || 0);

      if (action.payload && timeSinceLastToast > 500) {
        (state as any).lastErrorToastTime = now;
        toast.error("Failed to load users. Please try again.", {
          toastId: "users-load-error",
        });
      }
    });
    builder.addCase(fetchUsers.fulfilled, (state, action) => {
      state.users = action.payload.filtered;
      state.filteredUsers = action.payload.data;
      state.total = action.payload.total;
      state.loading = "succeeded";
    });
  },
});

export const { filterGeneral } = entityManageUsersSlice.actions;

export default entityManageUsersSlice.reducer;
